import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Button from "@components/ui/buttons/Button";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { useDataStore } from "@store/user_org";
import { useWhatsAppStatusStore } from "@store/whatsappStatusStore";
import { organizationsApi, whatsappApi } from "@services/api/organizations";
import type { WhatsAppTemplate, CreateWhatsAppTemplate } from "@services/api/organizations";
import { showNotification } from "context/notification.service";
import { LineChart } from "@components/charts/LineChart";
import type { LineChartDataPoint } from "@components/charts/LineChart";

function fmtEur(v: number, decimals = 2): string {
  return v.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + "\u00A0\u20AC";
}

export function WhatsAppSettings({ hideSoldeRecharge = false }: { hideSoldeRecharge?: boolean } = {}) {
  const orgData = useDataStore((s) => s.orgData);
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const wa = orgData?.whatsapp;
  const provider = wa?.provider || "wasender";

  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Live session health + in-app QR reconnection
  const sessionStatus = useWhatsAppStatusStore((s) => s.status);
  const fetchSessionStatus = useWhatsAppStatusStore((s) => s.fetch);
  const [qr, setQr] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const sessionDown = !!sessionStatus && !sessionStatus.connected;

  useEffect(() => {
    if (wa?.connected) fetchSessionStatus(true);
  }, [wa?.connected, fetchSessionStatus]);

  // Once a QR is displayed, poll so the card flips to "Connecté" right after the scan.
  useEffect(() => {
    if (!qr) return;
    const t = setInterval(async () => {
      await fetchSessionStatus(true);
      if (useWhatsAppStatusStore.getState().status?.connected) {
        setQr(null);
        showNotification({ type: "success", message: "WhatsApp reconnecté" });
      }
    }, 5000);
    return () => clearInterval(t);
  }, [qr, fetchSessionStatus]);

  const handleShowQr = async () => {
    setLoadingQr(true);
    setQrError(null);
    try {
      const res = await whatsappApi.getQr();
      // Always a rendered data-URL PNG — see WhatsAppStatusBanner for why.
      if (res.available && res.qr?.startsWith("data:")) {
        setQr(res.qr);
      } else {
        setQr(null);
        setQrError(res.reason || "unavailable");
      }
    } catch {
      setQr(null);
      setQrError("error");
    } finally {
      setLoadingQr(false);
    }
  };
  const [phoneInput, setPhoneInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  // Spending state
  type DayStat = {
    date: string;
    message_count: number;
    total_cost: number;
    error_count?: number;
    error_reasons?: Record<string, number>;
    by_line?: { mobile: number; fixe: number; autre: number };
    reasons_by_line?: Record<string, Record<string, number>>;
  };
  const [spending, setSpending] = useState<{
    solde: number;
    total_spent: number;
    transaction_count: number;
  }>({ solde: wa?.solde ?? 0, total_spent: wa?.total_spent ?? 0, transaction_count: 0 });
  const [dailyStats, setDailyStats] = useState<DayStat[]>([]);
  const [transactions, setTransactions] = useState<
    {
      id: string;
      message_type: string;
      unit_cost: number;
      created_at: string;
    }[]
  >([]);
  const [txPage, setTxPage] = useState(0);
  const TX_PER_PAGE = 20;
  const templateFormRef = useRef<HTMLDivElement>(null);

  // Template state
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingSyncing, setCreatingSyncing] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [formData, setFormData] = useState<CreateWhatsAppTemplate>({
    name: "",
    friendly_name: "",
    body: "",
    variables: {},
    buttons: [],
    quick_replies: [],
    category: "UTILITY",
  });

  const fetchSpending = useCallback(async () => {
    if (!wa?.connected) return;
    // Refetch org data to get fresh solde/total_spent
    if (orgData) fetchOrgData(orgData.id);
    try {
      const [s, daily, tx] = await Promise.all([
        whatsappApi.getSpending(),
        whatsappApi.getDailyStats(30),
        whatsappApi.getTransactions(TX_PER_PAGE, txPage * TX_PER_PAGE),
      ]);
      setSpending(s);
      setDailyStats(daily);
      setTransactions(tx);
    } catch {
      // API not available yet — use orgData values as fallback
      setSpending({
        solde: wa?.solde ?? 0,
        total_spent: wa?.total_spent ?? 0,
        transaction_count: 0,
      });
    }
  }, [wa?.connected, txPage]);

  useEffect(() => {
    fetchSpending();
  }, [fetchSpending]);

  const fetchTemplates = useCallback(async () => {
    if (!wa?.connected) return;
    setLoadingTemplates(true);
    try {
      const data = await whatsappApi.listTemplates();
      setTemplates(data);
    } catch {
      // API not available yet
    } finally {
      setLoadingTemplates(false);
    }
  }, [wa?.connected]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreateDefaults = async () => {
    setCreatingSyncing("defaults");
    try {
      const result = await whatsappApi.createDefaultTemplates();
      showNotification({
        type: "success",
        message: `${result.created_count} templates crees et soumis a Meta`,
      });
      await fetchTemplates();
    } catch (err: any) {
      showNotification({
        type: "error",
        message: err?.response?.data?.detail || "Erreur lors de la creation",
      });
    } finally {
      setCreatingSyncing(null);
    }
  };

  const handleSyncStatuses = async () => {
    setCreatingSyncing("sync");
    try {
      const result = await whatsappApi.syncTemplateStatuses();
      showNotification({
        type: "success",
        message: `${result.updated_count} statut(s) mis a jour`,
      });
      await fetchTemplates();
    } catch {
      showNotification({ type: "error", message: "Erreur de synchronisation" });
    } finally {
      setCreatingSyncing(null);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await whatsappApi.deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      showNotification({ type: "success", message: "Template supprime" });
    } catch {
      showNotification({ type: "error", message: "Erreur de suppression" });
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name || !formData.body) {
      showNotification({ type: "error", message: "Nom et contenu requis" });
      return;
    }
    try {
      if (editingTemplate) {
        const updated = await whatsappApi.updateTemplate(editingTemplate.id, formData);
        setTemplates((prev) => prev.map((t) => (t.id === editingTemplate.id ? { ...t, ...updated } : t)));
        showNotification({ type: "success", message: "Template mis a jour" });
      } else {
        const created = await whatsappApi.createTemplate(formData);
        setTemplates((prev) => [...prev, created]);
        showNotification({ type: "success", message: "Template cree et soumis a Meta" });
      }
      setShowCreateForm(false);
      setEditingTemplate(null);
      setFormData({ name: "", friendly_name: "", body: "", variables: {}, buttons: [], quick_replies: [], category: "UTILITY" });
    } catch (err: any) {
      showNotification({
        type: "error",
        message: err?.response?.data?.detail || "Erreur",
      });
    }
  };

  const openEditForm = (t: WhatsAppTemplate) => {
    setEditingTemplate(t);
    setFormData({
      name: t.name,
      friendly_name: t.friendly_name,
      body: t.body,
      variables: t.variables,
      buttons: t.buttons,
      quick_replies: t.quick_replies,
      category: t.category,
    });
    setShowCreateForm(true);
    setTimeout(() => templateFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const handleResubmit = async (id: string) => {
    setCreatingSyncing("resubmit");
    try {
      const updated = await whatsappApi.resubmitTemplate(id);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...updated } : t)));
      showNotification({ type: "success", message: updated.approval_status === "pending" ? "Template soumis a Meta" : "Echec de la soumission" });
    } catch {
      showNotification({ type: "error", message: "Erreur de soumission" });
    } finally {
      setCreatingSyncing(null);
    }
  };

  const statusColor = (s: string) => {
    switch (s) {
      case "approved": return { bg: "#F0FDF4", border: "#86EFAC", text: "#166534", label: "Approuve" };
      case "rejected": return { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", label: "Rejete" };
      case "pending": return { bg: "#FFFBEB", border: "#FCD34D", text: "#92400E", label: "En attente" };
      case "paused": return { bg: "#F9FAFB", border: "#E5E7EB", text: "#6B7280", label: "En pause" };
      default: return { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", label: "Non soumis" };
    }
  };

  const messagesChartData: LineChartDataPoint[] = useMemo(
    () =>
      dailyStats.map((d) => {
        const date = new Date(d.date);
        return {
          label: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", ""),
          tooltipLabel: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
          value: d.message_count,
        };
      }),
    [dailyStats]
  );

  // Echecs d'envoi (statut `failed` du journal) : un message facture qui n'arrive
  // jamais ne se voit pas sur la courbe des envois, d'ou sa propre courbe.
  const errorsChartData: LineChartDataPoint[] = useMemo(
    () =>
      dailyStats.map((d) => {
        const date = new Date(d.date);
        return {
          label: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", ""),
          tooltipLabel: date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
          value: d.error_count || 0,
        };
      }),
    [dailyStats]
  );

  // Motifs cumules sur la periode, separes par type de ligne : un fixe (01-05/09) n'a pas
  // de WhatsApp, son echec est attendu ; seul l'echec sur un mobile (06/07) est un vrai
  // probleme de livraison.
  const errorsByLine = useMemo(() => {
    const out = { mobile: 0, fixe: 0, autre: 0 };
    for (const d of dailyStats) {
      out.mobile += d.by_line?.mobile || 0;
      out.fixe += d.by_line?.fixe || 0;
      out.autre += d.by_line?.autre || 0;
    }
    return out;
  }, [dailyStats]);
  const reasonsByLine = useMemo(() => {
    const totals: Record<string, Record<string, number>> = {};
    for (const d of dailyStats) {
      for (const [line, reasons] of Object.entries(d.reasons_by_line || {})) {
        const bucket = (totals[line] ||= {});
        for (const [reason, n] of Object.entries(reasons)) bucket[reason] = (bucket[reason] || 0) + n;
      }
    }
    const sorted = (line: string) => Object.entries(totals[line] || {}).sort((a, b) => b[1] - a[1]);
    return { mobile: sorted("mobile"), fixe: [...sorted("fixe"), ...sorted("autre")] };
  }, [dailyStats]);
  const errorReasons = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const d of dailyStats) {
      for (const [reason, n] of Object.entries(d.error_reasons || {})) {
        totals[reason] = (totals[reason] || 0) + n;
      }
    }
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [dailyStats]);

  const handleConnect = async () => {
    if (!orgData || !phoneInput.trim()) return;
    setIsConnecting(true);
    try {
      const twilioNumber = phoneInput.trim().startsWith("whatsapp:")
        ? phoneInput.trim()
        : `whatsapp:${phoneInput.trim()}`;

      const displayPhone = phoneInput.trim().replace("whatsapp:", "");

      await whatsappApi.connect({
        twilio_phone_number: twilioNumber,
        business_name: nameInput.trim() || orgData.infos.name,
        display_phone: displayPhone,
      });
      await fetchOrgData(orgData.id);
      showNotification({
        type: "success",
        message: "WhatsApp connecté",
      });
      setPhoneInput("");
      setNameInput("");
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail || "Échec de la connexion WhatsApp";
      showNotification({ type: "error", message: detail });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    if (!orgData) return;
    try {
      await organizationsApi.update(orgData.id, {
        whatsapp: { enabled },
      });
      await fetchOrgData(orgData.id);
    } catch {
      showNotification({
        type: "error",
        message: "Échec de la mise à jour",
      });
    }
  };

  const handleDisconnect = async () => {
    if (!orgData) return;
    setIsDisconnecting(true);
    try {
      await whatsappApi.disconnect();
      await fetchOrgData(orgData.id);
      showNotification({ type: "success", message: "WhatsApp déconnecté" });
    } catch {
      showNotification({
        type: "error",
        message: "Échec de la déconnexion",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="flex flex-col">
      <h1
        className="text-hx font-semibold mb-2"
        style={{ fontFamily: "Poppins" }}
      >
        WhatsApp Business
      </h1>
      <p
        className="text-h2 text-neutral-500 mb-8"
        style={{ fontFamily: "Poppins" }}
      >
        {"Connectez le numéro WhatsApp Business de votre garage pour envoyer et recevoir des messages clients directement depuis l'application."}
      </p>

      {/* Status card — reflects the LIVE session state, not just the stored config.
          The config flag only says "a number was set up"; the session behind it can
          be dead (QR expired / phone logged out) while the config looks fine. */}
      {wa?.connected ? (
        <>
        <div
          className="flex flex-col gap-4 mb-8 p-6 rounded-xl"
          style={{
            background: sessionDown ? "#FEF2F2" : "#F0FDF4",
            border: `1px solid ${sessionDown ? "#FECACA" : "#BBF7D0"}`,
          }}
        >
          <div className="flex flex-row items-center gap-3">
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: sessionDown ? "#DC2626" : "#22C55E",
              }}
            />
            <span
              className="text-h1 font-semibold"
              style={{ fontFamily: "Poppins", color: sessionDown ? "#991B1B" : "#166534" }}
            >
              {sessionStatus ? sessionStatus.label : "Connecté"}
            </span>
            {sessionStatus?.checked_at && (
              <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                {`vérifié à ${new Date(sessionStatus.checked_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`}
              </span>
            )}
          </div>

          {sessionDown && (
            <div className="flex flex-col gap-3">
              <span className="text-h3" style={{ fontFamily: "Poppins", color: "#991B1B" }}>
                La session ne se rétablira pas seule. Tant qu'elle est déconnectée, les
                messages de vos clients n'arrivent pas dans l'application et sont
                définitivement perdus.
              </span>
              <div className="flex flex-row gap-2 items-center">
                <button
                  onClick={handleShowQr}
                  disabled={loadingQr}
                  className="text-h3 font-semibold px-4 py-2 rounded-lg"
                  style={{ fontFamily: "Poppins", background: "#25D366", color: "#FFFFFF", opacity: loadingQr ? 0.6 : 1 }}
                >
                  {loadingQr ? "Génération…" : "Afficher le QR code"}
                </button>
                <a
                  href={sessionStatus?.manage_url || "https://www.wasenderapi.com/whatsapp"}
                  target="_blank"
                  rel="noreferrer"
                  className="text-h3 font-medium px-4 py-2 rounded-lg"
                  style={{ fontFamily: "Poppins", color: "#374151", border: "1px solid #E5E7EB" }}
                >
                  Ouvrir WasenderAPI
                </a>
              </div>
              {qr && (
                <div className="flex flex-col gap-2 items-start p-4 rounded-xl" style={{ background: "#FFFFFF", border: "1px solid #E5E7EB" }}>
                  <span className="text-h4" style={{ fontFamily: "Poppins", color: "#374151" }}>
                    Sur le téléphone lié : WhatsApp → Réglages → Appareils connectés → Connecter un appareil
                  </span>
                  <img src={qr} alt="QR code WhatsApp" style={{ width: 240, height: 240 }} />
                  <button
                    onClick={handleShowQr}
                    className="text-h4 font-medium hover:underline"
                    style={{ fontFamily: "Poppins", color: "#25D366" }}
                  >
                    Nouveau QR code
                  </button>
                </div>
              )}
              {qrError && (
                <span className="text-h4" style={{ fontFamily: "Poppins", color: "#92400E" }}>
                  {qrError === "no_personal_token"
                    ? "QR code indisponible dans l'app : un jeton d'accès WasenderAPI doit être configuré (une seule fois). Utilisez « Ouvrir WasenderAPI » en attendant."
                    : "Impossible de récupérer le QR code — utilisez « Ouvrir WasenderAPI »."}
                </span>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <span
                className="text-h3 font-medium"
                style={{ fontFamily: "Poppins", color: "#6B7280", width: 140 }}
              >
                Entreprise
              </span>
              <span
                className="text-h2 font-semibold"
                style={{ fontFamily: "Poppins", color: "#111827" }}
              >
                {wa.business_name || "—"}
              </span>
            </div>
            {wa.display_phone && (
              <div className="flex flex-row items-center gap-2">
                <span
                  className="text-h3 font-medium"
                  style={{
                    fontFamily: "Poppins",
                    color: "#6B7280",
                    width: 140,
                  }}
                >
                  {"Numéro"}
                </span>
                <span
                  className="text-h2 font-medium"
                  style={{ fontFamily: "Poppins", color: "#111827" }}
                >
                  {wa.display_phone}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="text-h3 font-medium hover:underline self-start mt-2"
            style={{ fontFamily: "Poppins", color: "#EF4444" }}
          >
            {isDisconnecting ? "Déconnexion..." : "Déconnecter ce numéro"}
          </button>
        </div>

        <div className="flex flex-col gap-3 mb-8">
          <SwitchBrick
            checked={wa.enabled !== false}
            onChange={handleToggleEnabled}
            title="Envoi WhatsApp actif"
            description={
              "Coupe-circuit : désactivez pour bloquer TOUS les envois WhatsApp — messages de la messagerie, rappels automatiques et notifications clients — sans déconnecter le numéro. La réception n'est pas affectée."
            }
          />
          {wa.enabled === false && (
            <div
              className="flex flex-row items-center gap-2 px-4 py-3 rounded-lg"
              style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, background: "#D97706", flexShrink: 0 }} />
              <span className="text-h3 font-semibold" style={{ fontFamily: "Poppins", color: "#92400E" }}>
                Envoi coupé — aucun message WhatsApp ne partira vers vos clients.
              </span>
            </div>
          )}
        </div>

        {/* Solde & Consommation */}
        <div className="flex flex-col gap-6 mb-8">
            {hideSoldeRecharge && (
              <h2
                className="text-h1 font-semibold"
                style={{ fontFamily: "Poppins", color: "#111827" }}
              >
                Consommation
              </h2>
            )}
            {!hideSoldeRecharge && (
              <>
                <h2
                  className="text-h1 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#111827" }}
                >
                  Solde & Consommation
                </h2>

                {/* Solde + total spent + message count cards */}
                <div className="flex flex-row gap-4">
                  <div
                    className="flex flex-col flex-1 p-5 rounded-xl"
                    style={{
                      background: spending.solde > 0 ? "#F0FDF4" : "#FEF2F2",
                      border: `1px solid ${spending.solde > 0 ? "#BBF7D0" : "#FECACA"}`,
                    }}
                  >
                    <span
                      className="text-h3 font-medium mb-1"
                      style={{ fontFamily: "Poppins", color: "#6B7280" }}
                    >
                      Solde actuel
                    </span>
                    <span
                      className="text-hx font-bold"
                      style={{
                        fontFamily: "Poppins",
                        color: spending.solde > 0 ? "#166534" : "#991B1B",
                      }}
                    >
                      {fmtEur(spending.solde)}
                    </span>
                  </div>
                  <div
                    className="flex flex-col flex-1 p-5 rounded-xl"
                    style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
                  >
                    <span
                      className="text-h3 font-medium mb-1"
                      style={{ fontFamily: "Poppins", color: "#6B7280" }}
                    >
                      Total depense
                    </span>
                    <span
                      className="text-hx font-bold"
                      style={{ fontFamily: "Poppins", color: "#111827" }}
                    >
                      {fmtEur(spending.total_spent)}
                    </span>
                  </div>
                  <div
                    className="flex flex-col flex-1 p-5 rounded-xl"
                    style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
                  >
                    <span
                      className="text-h3 font-medium mb-1"
                      style={{ fontFamily: "Poppins", color: "#6B7280" }}
                    >
                      Messages envoyes
                    </span>
                    <span
                      className="text-hx font-bold"
                      style={{ fontFamily: "Poppins", color: "#111827" }}
                    >
                      {spending.transaction_count}
                    </span>
                  </div>
                </div>

              </>
            )}

            {/* Chart — messages sent */}
            <div
              className="p-5 rounded-xl"
              style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
            >
              <LineChart
                data={messagesChartData}
                title="Messages envoyes (30 jours)"
                valueSuffix=" msg"
                color="#25D366"
                emptyMessage="Aucun message sur cette periode"
                formatValue={(v) => `${v}`}
              />
            </div>

            {/* Chart — echecs d'envoi */}
            <div
              className="p-5 rounded-xl"
              style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
            >
              <LineChart
                data={errorsChartData}
                title="Messages en echec (30 jours)"
                valueSuffix=" erreur(s)"
                color="#EF4444"
                emptyMessage="Aucun echec sur cette periode"
                formatValue={(v) => `${v}`}
              />
              {errorReasons.length > 0 && (
                <div className="flex flex-col gap-4 mt-4 pt-4" style={{ borderTop: "1px solid #E5E7EB" }}>
                  {([
                    ["mobile", `Sur mobile (06/07) — auraient dû passer : ${errorsByLine.mobile}`, "#991B1B"],
                    ["fixe", `Sur fixe ou étranger — pas de WhatsApp, attendu : ${errorsByLine.fixe + errorsByLine.autre}`, "#6B7280"],
                  ] as const).map(([line, title, color]) => (
                    <div key={line} className="flex flex-col gap-1">
                      <span className="text-h4 font-medium mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                        {title}
                      </span>
                      {reasonsByLine[line].length === 0 && (
                        <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>Aucun</span>
                      )}
                      {reasonsByLine[line].map(([reason, count]) => (
                        <div key={reason} className="flex flex-row items-start justify-between gap-3">
                          <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                            {reason}
                          </span>
                          <span className="text-h4 font-medium flex-shrink-0" style={{ fontFamily: "Poppins", color }}>
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing info */}
            <div
              className="flex flex-col gap-1 px-4 py-3 rounded-lg"
              style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
            >
              <span
                className="text-h4 font-medium"
                style={{ fontFamily: "Poppins", color: "#6B7280" }}
              >
                Tarification par message
              </span>
              <span
                className="text-h4"
                style={{ fontFamily: "Poppins", color: "#9CA3AF" }}
              >
                0,015€ par message envoyé | 0,04€ par message template
              </span>
            </div>

            {/* Transaction history */}
            {transactions.length > 0 && (
              <div className="flex flex-col gap-3">
                <span
                  className="text-h2 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#374151" }}
                >
                  Historique des transactions
                </span>
                <table
                  className="w-full text-h3"
                  style={{ fontFamily: "Poppins", borderCollapse: "collapse" }}
                >
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E5E7EB" }}>
                      <th
                        className="text-left py-2 font-medium"
                        style={{ color: "#6B7280" }}
                      >
                        Date
                      </th>
                      <th
                        className="text-left py-2 font-medium"
                        style={{ color: "#6B7280" }}
                      >
                        Type
                      </th>
                      <th
                        className="text-right py-2 font-medium"
                        style={{ color: "#6B7280" }}
                      >
                        Cout
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr
                        key={tx.id}
                        style={{ borderBottom: "1px solid #F3F4F6" }}
                      >
                        <td className="py-2" style={{ color: "#374151" }}>
                          {new Date(tx.created_at).toLocaleString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="py-2" style={{ color: "#374151" }}>
                          {tx.message_type === "template"
                            ? "Hors fenetre"
                            : "En fenetre"}
                        </td>
                        <td
                          className="py-2 text-right"
                          style={{ color: "#374151" }}
                        >
                          {fmtEur(tx.unit_cost, 4)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="flex flex-row items-center gap-3 mt-1">
                  <button
                    onClick={() => setTxPage((p) => Math.max(0, p - 1))}
                    disabled={txPage === 0}
                    className="text-h3 font-medium px-3 py-1 rounded"
                    style={{
                      fontFamily: "Poppins",
                      color: txPage === 0 ? "#D1D5DB" : "#374151",
                      border: "1px solid #E5E7EB",
                      cursor: txPage === 0 ? "default" : "pointer",
                    }}
                  >
                    Precedent
                  </button>
                  <span
                    className="text-h4"
                    style={{ fontFamily: "Poppins", color: "#6B7280" }}
                  >
                    Page {txPage + 1}
                  </span>
                  <button
                    onClick={() => setTxPage((p) => p + 1)}
                    disabled={transactions.length < TX_PER_PAGE}
                    className="text-h3 font-medium px-3 py-1 rounded"
                    style={{
                      fontFamily: "Poppins",
                      color:
                        transactions.length < TX_PER_PAGE
                          ? "#D1D5DB"
                          : "#374151",
                      border: "1px solid #E5E7EB",
                      cursor:
                        transactions.length < TX_PER_PAGE
                          ? "default"
                          : "pointer",
                    }}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div
          className="flex flex-col gap-6 mb-8 p-8 rounded-xl"
          style={{
            background: "#FAFAFA",
            border: "1px solid #E5E7EB",
          }}
        >
          {/* WhatsApp logo */}
          <div className="flex items-center gap-4">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "#25D366",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="white"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span
                className="text-h1 font-semibold"
                style={{ fontFamily: "Poppins", color: "#111827" }}
              >
                Configurer WhatsApp
              </span>
              <span
                className="text-h3"
                style={{ fontFamily: "Poppins", color: "#6B7280" }}
              >
                {"Connectez votre numero WhatsApp pour envoyer et recevoir des messages."}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label
                className="text-h3 font-medium"
                style={{ fontFamily: "Poppins", color: "#374151" }}
              >
                {"Numéro WhatsApp"}
              </label>
              <input
                type="text"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="+14155238886"
                className="px-3 py-2 rounded-lg text-h2"
                style={{
                  fontFamily: "Poppins",
                  border: "1px solid #D1D5DB",
                  outline: "none",
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                className="text-h3 font-medium"
                style={{ fontFamily: "Poppins", color: "#374151" }}
              >
                Nom de l'entreprise (optionnel)
              </label>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder={orgData?.infos?.name || "Mon Garage"}
                className="px-3 py-2 rounded-lg text-h2"
                style={{
                  fontFamily: "Poppins",
                  border: "1px solid #D1D5DB",
                  outline: "none",
                }}
              />
            </div>
          </div>

          <Button
            label={isConnecting ? "Connexion en cours..." : "Connecter"}
            onClick={handleConnect}
            disabled={isConnecting || !phoneInput.trim()}
          />
        </div>
      )}

      {/* Templates section — only visible when using Twilio provider */}
      {wa?.connected && provider === "twilio" && (
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex flex-row items-center justify-between">
            <h2
              className="text-h1 font-semibold"
              style={{ fontFamily: "Poppins", color: "#111827" }}
            >
              Templates WhatsApp
            </h2>
            <div className="flex flex-row gap-2">
              <button
                onClick={handleSyncStatuses}
                disabled={creatingSyncing !== null}
                className="text-h3 font-medium px-3 py-1.5 rounded-lg"
                style={{
                  fontFamily: "Poppins",
                  border: "1px solid #E5E7EB",
                  color: "#374151",
                  opacity: creatingSyncing ? 0.5 : 1,
                }}
              >
                {creatingSyncing === "sync" ? "Synchro..." : "Actualiser statuts"}
              </button>
              <button
                onClick={handleCreateDefaults}
                disabled={creatingSyncing !== null}
                className="text-h3 font-medium px-3 py-1.5 rounded-lg"
                style={{
                  fontFamily: "Poppins",
                  background: "#25D366",
                  color: "#FFFFFF",
                  border: "none",
                  opacity: creatingSyncing ? 0.5 : 1,
                }}
              >
                {creatingSyncing === "defaults" ? "Creation..." : "Creer templates par defaut"}
              </button>
            </div>
          </div>

          <p
            className="text-h3"
            style={{ fontFamily: "Poppins", color: "#6B7280", marginTop: -8 }}
          >
            Les templates sont soumis a Meta pour approbation. Une fois approuves, ils peuvent etre envoyes hors de la fenetre de 24h.
          </p>

          {/* Template list */}
          {loadingTemplates ? (
            <span className="text-h3" style={{ color: "#9CA3AF" }}>Chargement...</span>
          ) : templates.length === 0 ? (
            <div
              className="flex flex-col items-center gap-3 py-8 rounded-xl"
              style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
            >
              <span className="text-h2 font-medium" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                Aucun template
              </span>
              <span className="text-h3" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>
                Cliquez sur "Creer templates par defaut" pour commencer
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {templates.map((t) => {
                const st = statusColor(t.approval_status);
                return (
                  <div
                    key={t.id}
                    className="flex flex-row items-center justify-between p-4 rounded-lg"
                    style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}
                  >
                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                      <div className="flex flex-row items-center gap-2">
                        <span
                          className="text-h2 font-semibold"
                          style={{ fontFamily: "Poppins", color: "#111827" }}
                        >
                          {t.friendly_name || t.name}
                        </span>
                        <span
                          className="text-h4 font-medium px-2 py-0.5 rounded-full"
                          style={{
                            background: st.bg,
                            border: `1px solid ${st.border}`,
                            color: st.text,
                          }}
                        >
                          {st.label}
                        </span>
                      </div>
                      <span
                        className="text-h4 truncate"
                        style={{ fontFamily: "Poppins", color: "#6B7280", maxWidth: 500 }}
                      >
                        {t.body}
                      </span>
                      {t.quick_replies.length > 0 && (
                        <div className="flex flex-row gap-1 mt-1">
                          {t.quick_replies.map((qr) => (
                            <span
                              key={qr}
                              className="text-h4 px-2 py-0.5 rounded-full"
                              style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}
                            >
                              {qr}
                            </span>
                          ))}
                        </div>
                      )}
                      {t.buttons.length > 0 && (
                        <div className="flex flex-row gap-1 mt-1">
                          {t.buttons.map((btn, i) => (
                            <span
                              key={i}
                              className="text-h4 px-2 py-0.5 rounded-full"
                              style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #86EFAC" }}
                            >
                              {btn.title}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row gap-2 ml-4">
                      {(t.approval_status === "error" || !t.content_sid) && (
                        <button
                          onClick={() => handleResubmit(t.id)}
                          disabled={creatingSyncing !== null}
                          className="text-h4 font-medium px-2 py-1 rounded"
                          style={{ color: "#FFFFFF", background: "#25D366", opacity: creatingSyncing ? 0.5 : 1 }}
                        >
                          Soumettre
                        </button>
                      )}
                      <button
                        onClick={() => openEditForm(t)}
                        className="text-h4 font-medium px-2 py-1 rounded"
                        style={{ color: "#6B7280", border: "1px solid #E5E7EB" }}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(t.id)}
                        className="text-h4 font-medium px-2 py-1 rounded"
                        style={{ color: "#EF4444", border: "1px solid #FECACA" }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Create button */}
          <button
            onClick={() => {
              setEditingTemplate(null);
              setFormData({ name: "", friendly_name: "", body: "", variables: {}, buttons: [], quick_replies: [], category: "UTILITY" });
              setShowCreateForm(true);
            }}
            className="text-h3 font-medium px-4 py-2 rounded-lg self-start"
            style={{ fontFamily: "Poppins", border: "1px solid #D1D5DB", color: "#374151" }}
          >
            + Nouveau template
          </button>

          {/* Create/Edit modal */}
          {showCreateForm && createPortal(
            <div
              className="fixed inset-0 z-50 flex items-center justify-center"
              style={{ background: "rgba(0,0,0,0.4)" }}
            >
              <div
                ref={templateFormRef}
                className="flex flex-col gap-4 p-6 rounded-2xl"
                style={{ background: "#FFFFFF", width: 520, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
              >
                <span className="text-h1 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>
                  {editingTemplate ? "Modifier le template" : "Nouveau template"}
                </span>
                <div className="flex flex-row gap-3">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-h4 font-medium" style={{ color: "#374151" }}>Nom technique</label>
                    <input
                      value={formData.name}
                      onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                      placeholder="mon_template"
                      disabled={!!editingTemplate}
                      className="px-3 py-2 rounded-lg text-h3"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-h4 font-medium" style={{ color: "#374151" }}>Pseudo/Prénom (court)</label>
                    <input
                      value={formData.friendly_name}
                      onChange={(e) => setFormData((f) => ({ ...f, friendly_name: e.target.value }))}
                      placeholder="Mon template"
                      className="px-3 py-2 rounded-lg text-h3"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins" }}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-h4 font-medium" style={{ color: "#374151" }}>
                    {"Contenu (utilisez {{1}}, {{2}}, etc. pour les variables)"}
                  </label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => setFormData((f) => ({ ...f, body: e.target.value }))}
                    placeholder={"Bonjour {{1}}, votre {{2}} est pret."}
                    rows={4}
                    className="px-3 py-2 rounded-lg text-h3"
                    style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins", resize: "vertical" }}
                  />
                </div>
                <div className="flex flex-row gap-3">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-h4 font-medium" style={{ color: "#374151" }}>
                      Reponses rapides (separees par virgule)
                    </label>
                    <input
                      value={(formData.quick_replies || []).join(", ")}
                      onChange={(e) => {
                        const qrs = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                        setFormData((f) => ({ ...f, quick_replies: qrs }));
                      }}
                      placeholder="Merci, Une question"
                      className="px-3 py-2 rounded-lg text-h3"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1" style={{ width: 160 }}>
                    <label className="text-h4 font-medium" style={{ color: "#374151" }}>Categorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData((f) => ({ ...f, category: e.target.value }))}
                      className="px-3 py-2 rounded-lg text-h3"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins" }}
                    >
                      <option value="UTILITY">Utility</option>
                      <option value="MARKETING">Marketing</option>
                    </select>
                  </div>
                </div>
                {/* CTA Button config */}
                <div className="flex flex-col gap-2">
                  <label className="text-h4 font-medium" style={{ color: "#374151" }}>
                    Bouton d'action (optionnel)
                  </label>
                  <div className="flex flex-row gap-3">
                    <input
                      value={(formData.buttons || [])[0]?.title || ""}
                      onChange={(e) => {
                        const title = e.target.value;
                        if (!title) {
                          setFormData((f) => ({ ...f, buttons: [] }));
                        } else {
                          const existing = (formData.buttons || [])[0] || { type: "URL", url: "" };
                          setFormData((f) => ({ ...f, buttons: [{ ...existing, type: "URL", title }] }));
                        }
                      }}
                      placeholder="Titre du bouton (ex: Suivre mon dossier)"
                      className="px-3 py-2 rounded-lg text-h3 flex-1"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins" }}
                    />
                    <input
                      value={(formData.buttons || [])[0]?.url || ""}
                      onChange={(e) => {
                        const url = e.target.value;
                        const existing = (formData.buttons || [])[0] || { type: "URL", title: "" };
                        setFormData((f) => ({ ...f, buttons: [{ ...existing, type: "URL", url }] }));
                      }}
                      placeholder={"Variable URL (ex: {{3}})"}
                      className="px-3 py-2 rounded-lg text-h3"
                      style={{ border: "1px solid #D1D5DB", outline: "none", fontFamily: "Poppins", width: 160 }}
                    />
                  </div>
                  <span className="text-h4" style={{ color: "#9CA3AF" }}>
                    {"Le bouton apparait sous le message WhatsApp. L'URL utilise une variable (ex: {{3}}) remplie automatiquement avec le lien de suivi."}
                  </span>
                </div>
                <div className="flex flex-row gap-2 mt-2">
                  <Button label={editingTemplate ? "Enregistrer" : "Creer et soumettre"} onClick={handleSaveTemplate} />
                  <button
                    onClick={() => { setShowCreateForm(false); setEditingTemplate(null); }}
                    className="text-h3 font-medium px-4 py-2 rounded-lg"
                    style={{ fontFamily: "Poppins", color: "#6B7280" }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
        </div>
      )}

      {/* How it works */}
      <div
        className="flex flex-col gap-4 p-5 rounded-lg"
        style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}
      >
        <span
          className="text-h2 font-semibold"
          style={{ fontFamily: "Poppins", color: "#374151" }}
        >
          {"Comment ça marche"}
        </span>
        <div className="flex flex-col gap-3">
          {[
            {
              step: "1",
              title: "Configuration",
              desc: "Connectez votre numéro WhatsApp depuis cette page pour activer la messagerie avec vos clients.",
            },
            {
              step: "2",
              title: "Réception automatique",
              desc: "Les messages WhatsApp de vos clients apparaissent en temps réel dans la conversation du dossier correspondant.",
            },
            {
              step: "3",
              title: "Réponse depuis l'app",
              desc: "Répondez directement depuis l'onglet \"WhatsApp\" du chat — le client reçoit votre réponse sur WhatsApp.",
            },
            {
              step: "4",
              title: "Solde prépayé",
              desc: "Rechargez votre solde WhatsApp pour envoyer des messages. Chaque message envoyé est débité de votre solde.",
            },
          ].map((item) => (
            <div key={item.step} className="flex flex-row gap-3">
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#25D366",
                  color: "#FFFFFF",
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {item.step}
              </div>
              <div className="flex flex-col gap-0.5">
                <span
                  className="text-h3 font-semibold"
                  style={{ fontFamily: "Poppins", color: "#374151" }}
                >
                  {item.title}
                </span>
                <span
                  className="text-h4"
                  style={{ fontFamily: "Poppins", color: "#6B7280" }}
                >
                  {item.desc}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
